import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Json } from "../core";

// DialogActionStack — global FILO stack of "currently open dialog actions".
//
// Any caller can `push({actionSlug, recordRef})` to overlay an action in
// a Radix Dialog. The stack supports nested opens: clicking a "Configure
// X" button inside an already-open dialog mounts a NEW dialog on top of
// the current one. Closing the top dialog pops the stack.
//
// The stack lives at the workspace shell level so it survives action
// switches (the user's underlying page changes, but the open dialog
// stays). Each entry is keyed so a re-open of the same action+record
// doesn't create a duplicate overlay.

export interface DialogActionFrame {
    actionSlug: string;
    /** Optional target record uuid — for window actions opening a single
        form, this is the record to load. Empty / undefined means the
        action's default behaviour (open the list / open a new draft). */
    recordRef?: string;
    /** Optional initial view mode. When omitted the action's default_view
        is used. The DialogActionHost currently only supports "form"; the
        prop is here for future extensibility (kanban inside a dialog). */
    viewMode?: "form" | "list" | "kanban";
    /** Seed values for a new-record draft (Enhancement 10 record-bound
        actions) — e.g. {user_id: <active record uuid>} derived from the
        action's `context` template. The DialogActionHost passes these as the
        form's initial record so the wizard opens pre-filled. */
    defaults?: Record<string, Json>;
    /** Called once after a successful save of this dialog's record, with the
        saved values. Lets the opener react to the save — e.g. a record-bound
        action that ends the current session when an admin changes their OWN
        password. Not called on error or cancel. */
    onSaved?: (savedValues: Record<string, Json>) => void;
}

interface DialogActionStackApi {
    stack: DialogActionFrame[];
    push: (frame: DialogActionFrame) => void;
    pop: () => void;
    /** Pop a specific frame by its index (defensive — usually a frame
        knows its own index and calls `pop()` when closing the topmost). */
    closeAt: (index: number) => void;
    /** Clear the whole stack — useful for logout / page-level resets. */
    clear: () => void;
}

const Ctx = createContext<DialogActionStackApi | null>(null);

export function DialogActionStackProvider({ children }: { children: ReactNode }) {
    const [stack, setStack] = useState<DialogActionFrame[]>([]);

    const push = useCallback((frame: DialogActionFrame) => {
        setStack((prev) => [...prev, frame]);
    }, []);

    const pop = useCallback(() => {
        setStack((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
    }, []);

    const closeAt = useCallback((index: number) => {
        setStack((prev) => (index >= 0 && index < prev.length ? prev.slice(0, index) : prev));
    }, []);

    const clear = useCallback(() => setStack([]), []);

    const api = useMemo<DialogActionStackApi>(
        () => ({ stack, push, pop, closeAt, clear }),
        [stack, push, pop, closeAt, clear],
    );

    return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useDialogActionStack(): DialogActionStackApi {
    const ctx = useContext(Ctx);
    if (!ctx) {
        throw new Error(
            "useDialogActionStack must be used inside <DialogActionStackProvider>. " +
            "Wrap the workspace shell with the provider first.",
        );
    }
    return ctx;
}
